import { EARTH_EQUATORIAL_RADIUS, EARTH_POLAR_RADIUS } from "./constants";

import type { Tuple2, Tuple3 } from "@skydio/core";
import type { GPSPoint, LngLat } from "./types/geospatial";
import type { Meters } from "./types/semantic";

// A typescript implementation of the GPS linearizer used on vehicle.
//
// This implementation exactly follows the C++ implementation in util/body/gps_linearizer.h, and is
// asserted in a unit test to have the same behavior. See there for more commentary about the
// approach.
//
// Usage notes:
// - The Euclidean coordinate system is an ENU system, so for use with mapbox, you will need to
//   convert the result to EUS.
// - This should be used instead of the mercator-based linearization functions in `mercator.ts` if
//   (a) interpreting local coordinates that were oriignally produced using the vehicle linearizer
//   or (b) producing local coordinates that are then consumed by the vehicle linearizer. This is
//   because the two linearizations differ significantly at any nontrivial distance from the
//   linearization point. E.g., if querying an object's local coordintes from GQL to display in
//   mapbox that were originally computed uising the vehicle linearizer, then they should be
//   converted to global coordinates using this linearizer before being converted into the mapbox
//   scene frame using the mercator-based linearization (EVEN IF the scene origin corresponds to
//   the object's local coordinate system origin).
export class GpsLinearizer {
  public readonly origin: GPSPoint;
  readonly radiusNs: number;
  readonly radiusEw: number;

  constructor(origin: GPSPoint) {
    this.origin = origin;

    const originLatRad = this.origin.lat * (Math.PI / 180);
    const denom =
      (EARTH_EQUATORIAL_RADIUS * Math.cos(originLatRad)) ** 2 +
      (EARTH_POLAR_RADIUS * Math.sin(originLatRad)) ** 2;

    this.radiusNs = (EARTH_EQUATORIAL_RADIUS * EARTH_POLAR_RADIUS) ** 2 / Math.pow(denom, 1.5);
    this.radiusEw = (EARTH_EQUATORIAL_RADIUS * EARTH_EQUATORIAL_RADIUS) / Math.sqrt(denom);
  }

  public gpsToXy(coord: LngLat): Tuple2<Meters> {
    const dLat = (coord.lat - this.origin.lat) * (Math.PI / 180);
    const dLng = (coord.lng - this.origin.lng) * (Math.PI / 180);
    const originLatRad = this.origin.lat * (Math.PI / 180);
    const x = Math.sin(dLng) * this.radiusEw * Math.cos(originLatRad);
    const y = Math.sin(dLat) * this.radiusNs;
    return [x, y];
  }

  public gpsToXyz(coord: GPSPoint): Tuple3<Meters> {
    const [x, y] = this.gpsToXy({ lat: coord.lat, lng: coord.lng });
    const z = this.origin.alt && coord.alt ? coord.alt - this.origin.alt : 0;
    return [x, y, z];
  }

  public xyToLatLon(coord: Tuple2<Meters>): LngLat {
    const [x, y] = coord;
    const dLat = Math.asin(y / this.radiusNs);
    const dLng = Math.asin(x / (this.radiusEw * Math.cos(this.origin.lat * (Math.PI / 180))));
    const lat = this.origin.lat + (dLat * 180) / Math.PI;
    const lng = this.origin.lng + (dLng * 180) / Math.PI;
    return { lat: lat, lng: lng };
  }

  public xyzToLatLonAlt(coord: Tuple3<Meters>): GPSPoint {
    const [x, y, z] = coord;
    const lat_lon = this.xyToLatLon([x, y]);
    return {
      lat: lat_lon.lat,
      lng: lat_lon.lng,
      alt: this.origin.alt ? z + this.origin.alt : undefined,
    };
  }
}
